import axios from 'axios';
import { API_BASE } from './config';

/**
 * Fetch wallets with pagination and filtering
 * @param filters Filter criteria
 * @param limit Maximum number of wallets to return
 * @param skip Number of wallets to skip (for pagination)
 * @param searchTerm Search term for wallet address or user ID
 * @returns Wallets matching the criteria
 */
export const fetchWallets = async (
  filters: any = {},
  limit: number = 50,
  skip: number = 0,
  searchTerm: string = ''
) => {
  // Trim the search term to ensure it's clean
  const trimmedSearchTerm = searchTerm.trim();
  
  const response = await axios.post(`${API_BASE}/wallet/admin/Wallets`, {
    filters,
    limit,
    skip,
    searchTerm: trimmedSearchTerm || '' // Ensure we always send a string, even if empty
  });
  return response.data;
};

/**
 * Fetch hot wallets by network and coin
 * @param networkId Network ID or full_name
 * @param coinId Coin ID or symbol
 * @param limit Maximum number of wallets to return
 * @returns Hot wallets for the specified network and coin
 */
export const fetchHotWalletsByNetworkAndCoin = async (
  networkId: string,
  coinId: string,
  limit: number = 20
) => {
  console.log('Fetching hot wallets for network:', networkId, 'and coin:', coinId);

  try {
    // Based on our testing, we found that filtering by network_full_name and coin_symbol
    // doesn't work as expected. Instead, we'll fetch all hot wallets and filter them in memory.
    const response = await axios.post(`${API_BASE}/wallet/admin/Wallets`, {
      filters: {
        wallet_type: 'hot',
        status: 'Active'
      },
      limit: 100, // Fetch more to ensure we have enough after filtering
      skip: 0
    });

    console.log('Hot wallets API response received, filtering results...');

    // Filter the wallets in memory
    let wallets = response.data.data?.wallets || [];

    if (networkId) {
      wallets = wallets.filter((wallet: any) =>
        wallet.network_full_name === networkId
      );
    }

    if (coinId) {
      wallets = wallets.filter((wallet: any) =>
        wallet.coin_symbol === coinId
      );
    }

    // Limit the results
    wallets = wallets.slice(0, limit);

    console.log(`Filtered to ${wallets.length} wallets matching criteria`);

    // Return in the same format as the original response
    return {
      ...response.data,
      data: {
        ...response.data.data,
        wallets,
        pagination: {
          total: wallets.length,
          limit,
          skip: 0,
          hasMore: false
        }
      }
    };
  } catch (error: any) {
    console.error('Error fetching hot wallets:', error);
    return {
      data: { wallets: [] },
      status: false,
      error: error.message || 'Failed to fetch hot wallets'
    };
  }
};

/**
 * Fetch wallets with light data (less fields)
 * @param limit Maximum number of wallets to return
 * @param skip Number of wallets to skip (for pagination)
 * @returns Light wallet data
 */
export const fetchWalletsLight = async (limit: number = 50, skip: number = 0) => {
  const response = await axios.post(`${API_BASE}/wallet/admin/Wallets/light`, {
    limit,
    skip
  });
  return response.data;
};

/**
 * Fetch a wallet by ID
 * @param id Wallet ID
 * @returns Wallet details
 */
export const fetchWalletById = async (id: string) => {
  // Use POST request with filters to get wallet by ID (matches legacy dashboard)
  const response = await axios.post(`${API_BASE}/wallet/admin/Wallets`, {
    filters: { _id: id },
    limit: 1,
    skip: 0
  });

  // Extract the wallet from the response
  const data = response.data.data || response.data;
  if (data && data.wallets && data.wallets.length > 0) {
    return { data: data.wallets[0] };
  }

  throw new Error('Wallet not found');
};

/**
 * Create a new wallet
 * @param data Wallet creation data
 * @returns Created wallet
 */
export const createWallet = async (data: {
  network: string;
  is_evm: number;
  coin: string;
  userid: number;
  is_new?: number; // Made optional to allow backend to determine whether to return existing wallet
  type?: string;
  status?: string;
  memo?: string | null;
  coin_network_id?: string;
}) => {
  // Wallet creation requires Tatum monitoring setup, which can take up to 60 seconds
  // Use a longer timeout (90 seconds) to accommodate this
  const WALLET_CREATION_TIMEOUT = parseInt(
    process.env.REACT_APP_WALLET_CREATION_TIMEOUT || '90000',
    10
  );
  
  const response = await axios.post(`${API_BASE}/wallet/GetWalletAddress`, data, {
    timeout: WALLET_CREATION_TIMEOUT
  });
  return response.data;
};

/**
 * Update wallet status
 * @param id Wallet ID
 * @param status New status
 * @returns Updated wallet
 */
export const updateWalletStatus = async (id: string, status: string) => {
  // Use PATCH method with the same endpoint as the legacy dashboard
  const response = await axios.patch(`${API_BASE}/wallet/admin/Wallets/${id}/status`, {
    status
  });
  return response.data;
};

/**
 * Get supported networks
 * @returns List of supported networks
 */
export const getSupportedNetworks = async () => {
  try {
    // Use the same endpoint as the coin networks page
    const response = await axios.get(`${API_BASE}/network`);
    console.log('Network API response in wallet.ts:', response.data);

    // Handle different response formats
    const data = response.data;

    // Return the data in a consistent format
    if (data && data.data && Array.isArray(data.data)) {
      // Standard format with data property
      return data;
    } else if (Array.isArray(data)) {
      // If the response is already an array, wrap it in standard format
      return {
        data: data,
        status: true
      };
    } else if (data && data.networks && Array.isArray(data.networks)) {
      // If the response has a networks property that's an array
      return {
        data: data.networks,
        status: true
      };
    } else {
      // If we can't determine the format, return the raw data
      console.warn('Unknown network data format in wallet.ts:', data);
      return {
        data: [],
        status: false,
        error: 'Invalid network data format'
      };
    }
  } catch (error: any) {
    console.error('Error fetching networks in wallet.ts:', error);
    return {
      data: [],
      status: false,
      error: error.message || 'Failed to fetch networks'
    };
  }
};

/**
 * Get supported coins for wallet management
 * @returns List of supported coins
 */
export const getWalletCoins = async () => {
  try {
    const response = await axios.get(`${API_BASE}/coin`);
    console.log('Coin API response in wallet.ts:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching coins in wallet.ts:', error);
    return {
      data: [],
      status: false,
      error: error.message || 'Failed to fetch coins'
    };
  }
};

/**
 * Get networks for wallet management
 * @returns List of networks
 */
export const getWalletNetworks = async () => {
  try {
    const response = await axios.get(`${API_BASE}/network`);
    console.log('Network API response in getWalletNetworks:', response.data);

    // Handle different response formats
    const data = response.data;

    // Return the data in a consistent format
    if (data && data.data && Array.isArray(data.data)) {
      // Standard format with data property
      return data;
    } else if (Array.isArray(data)) {
      // If the response is already an array, wrap it in standard format
      return {
        data: data,
        status: true
      };
    } else if (data && data.networks && Array.isArray(data.networks)) {
      // If the response has a networks property that's an array
      return {
        data: data.networks,
        status: true
      };
    } else {
      // If we can't determine the format, return the raw data
      console.warn('Unknown network data format in getWalletNetworks:', data);
      return {
        data: [],
        status: false,
        error: 'Invalid network data format'
      };
    }
  } catch (error: any) {
    console.error('Error fetching networks in getWalletNetworks:', error);
    return {
      data: [],
      status: false,
      error: error.message || 'Failed to fetch networks'
    };
  }
};

/**
 * Get coin networks for wallet management
 * @returns List of coin networks
 */
export const getWalletCoinNetworks = async () => {
  try {
    const response = await axios.get(`${API_BASE}/coin-network`);
    console.log('Coin network API response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching coin networks:', error);
    return {
      data: [],
      status: false,
      error: error.message || 'Failed to fetch coin networks'
    };
  }
};

/**
 * Get wallet balance
 * @param address Wallet address
 * @param network Network name
 * @param is_evm Whether the network is EVM-compatible
 * @param coin_symbol Optional coin symbol
 * @returns Wallet balance
 */
export const getWalletBalance = async (
  address: string,
  network: string,
  is_evm: number,
  coin_symbol?: string
) => {
  try {
    const data: any = {
      address,
      network,
      is_evm
    };

    if (coin_symbol) {
      data.coin = coin_symbol;
    }

    console.log('Wallet balance request data:', data);
    const response = await axios.post(`${API_BASE}/wallet/Balance`, data);
    console.log('Wallet balance response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching wallet balance:', error);
    return {
      status: false,
      error: error.message || 'Failed to fetch wallet balance'
    };
  }
};

/**
 * Get all balances for a wallet (native + tokens)
 * @param address Wallet address
 * @param network Network name
 * @param is_evm Whether the network is EVM-compatible
 * @returns All balances for the wallet
 */
export const getAllWalletBalances = async (
  address: string,
  network: string,
  is_evm: number
) => {
  try {
    const data = {
      address,
      network,
      is_evm
    };

    console.log('All wallet balances request data:', data);
    const response = await axios.post(`${API_BASE}/wallet/AllBalances`, data);
    console.log('All wallet balances response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching all wallet balances:', error);
    return {
      status: false,
      error: error.message || 'Failed to fetch all wallet balances'
    };
  }
};

/**
 * Get user balance
 * @param userId User ID
 * @returns User balance for all coins
 */
export const getUserBalance = async (userId: number) => {
  try {
    console.log('User balance request for user ID:', userId);
    const response = await axios.post(`${API_BASE}/wallet/GetUserBalance`, {
      userid: userId
    });
    console.log('User balance response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching user balance:', error);
    return {
      status: false,
      error: error.message || 'Failed to fetch user balance'
    };
  }
};
